/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';

import { isValidPhoneNumber } from 'libphonenumber-js';
import FormInput from '../../components/forms/form-input';
import PhoneField from '../../components/forms/phone-field';
import FormSelect from '../../components/forms/form-select';
import FormMultiSelect from '../../components/forms/form-multi-select';
import FormSearchableSelect from '../../components/forms/form-searchable-select';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import ScreenHeader from '../../components/common/screen-header';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';

import { usersService, type UserCreateRequest, type UserUpdateRequest } from '../../services/users.service';
import { personnelLookupService, type PersonnelMinimal } from '../../services/personnel-lookup.service';
import { projectsService, type Project } from '../../services/projects.service';
import { useProjectScope } from '../../hooks/useProjectScope';
import { extractErrorMessage } from '../../utils/error-handler';

// -----------------------------------------------------------------------------
// Zod Schema (mirrors backend UserMasterCreate/UpdateSchema)
// -----------------------------------------------------------------------------
const userSchema = z
  .object({
    // Personnel is optional: a user MAY be a personnel, or be standalone.
    personnelMasterId: z.string().trim().optional().or(z.literal('')),
    // Single owning project. On CREATE it mirrors the first of `appIds` (so this
    // required check doubles as "at least one project"); on EDIT it is the only one.
    appId: z.string().trim().min(1, 'Project is required'),
    // CREATE only: the full set of projects to create the user in (one row each).
    appIds: z.array(z.string()).optional(),
    // Username: any non-empty string, unique per app (was previously restricted
    // to exactly 8 digits — relaxed per requirement).
    userId: z
      .string()
      .trim()
      .min(1, 'Username is required')
      .max(50, 'Username must be ≤ 50 characters'),
    // Contact — required only for a standalone user (no personnel). When a
    // personnel is linked, contact is derived from it on the backend.
    contactEmail: z.string().trim().optional().or(z.literal('')),
    contactPhone: z.string().trim().optional().or(z.literal('')),
    mpin: z
      .string()
      .trim()
      .regex(/^\d{4,6}$/, 'MPIN must be 4-6 digits')
      .optional()
      .or(z.literal('')),
    password: z.string().optional().or(z.literal('')),
    defaultlanguageId: z
      .union([z.string(), z.number()])
      .optional()
      .refine(
        (v) => v === undefined || v === '' || /^\d+$/.test(String(v)),
        'Language ID must be a number'
      ),
    // Edit-only fields
    userStatus: z.string().trim().max(100, 'Must be ≤ 100 characters').optional().or(z.literal('')),
    reasonForBlock: z.string().trim().max(500, 'Must be ≤ 500 characters').optional().or(z.literal('')),
    blocked: z.boolean().optional(),
    isRegistered: z.boolean().optional(),
    isVerified: z.boolean().optional(),
    isLoggedIn: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const hasPersonnel = !!(data.personnelMasterId && data.personnelMasterId.trim());
    if (hasPersonnel) return;
    // Standalone user — contact email + phone are mandatory.
    const email = (data.contactEmail || '').trim();
    const phone = (data.contactPhone || '').trim();
    if (!email) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['contactEmail'], message: 'Email is required' });
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['contactEmail'], message: 'Enter a valid email address' });
    }
    if (!phone) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['contactPhone'], message: 'Phone is required' });
    } else if (!isValidPhoneNumber(phone)) {
      // PhoneField emits E.164, so a self-contained validity check enforces the
      // country-correct digit count without needing a separate country field.
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['contactPhone'], message: 'Enter a valid phone number for the selected country' });
    }
  });

export type UserFormData = z.infer<typeof userSchema>;

// On CREATE a user needs a way to sign in, so at least one of MPIN / Password
// must be set. Not applied on edit, where credentials are changed through their
// own set/reset flows and both fields are absent from this form.
const userCreateSchema = userSchema.superRefine((data, ctx) => {
  const hasMpin = !!(data.mpin && data.mpin.trim());
  const hasPassword = !!(data.password && data.password.trim());
  if (!hasMpin && !hasPassword) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'Set a password or an MPIN' });
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['mpin'], message: 'Set an MPIN or a password' });
  }
});

// Supported app languages. `value` is the numeric defaultlanguageId stored on the
// user_master record; `label` is the readable name shown in the dropdown.
// (Adjust the id↔language mapping here if the app expects different codes.)
const LANGUAGE_OPTIONS = [
  { value: '1', label: 'English' },
  { value: '2', label: 'Telugu' },
  { value: '3', label: 'Hindi' },
  { value: '4', label: 'Urdu' },
];

const UserManagementForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'userManagement',
    basePath: '/user-management',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  // Dropdown data
  const { data: personnel = [] } = useQuery({
    queryKey: ['personnel', 'lookup'],
    queryFn: personnelLookupService.getAll,
  });
  const { data: apps = [] } = useQuery({
    queryKey: ['apps', 'dropdown'],
    queryFn: projectsService.getAllForDropdown,
  });

  const appOptions = useMemo(
    () =>
      apps.map((a: Project) => ({
        value: a._id,
        label: a.appCode ? `${a.name} (${a.appCode})` : a.name,
      })),
    [apps]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<UserFormData>({
    // Create adds the "MPIN or Password required" rule; edit uses the base schema
    // (those fields aren't on the edit form).
    resolver: zodResolver(isEditMode ? userSchema : userCreateSchema) as Resolver<UserFormData>,
    defaultValues: {
      personnelMasterId: '',
      appId: '',
      appIds: [],
      userId: '',
      contactEmail: '',
      contactPhone: '',
      mpin: '',
      password: '',
      defaultlanguageId: '',
      userStatus: '',
      reasonForBlock: '',
      blocked: false,
      isRegistered: false,
      isVerified: false,
      isLoggedIn: false,
    },
    mode: 'onChange',
  });

  // A linked personnel must be in the same project as the user, or global.
  const selectedAppId = watch('appId');
  const { belongsToProject, projectTag } = useProjectScope(selectedAppId, apps as any);

  const personnelOptions = useMemo(
    () =>
      personnel
        .filter((p: PersonnelMinimal) => belongsToProject(p.appId))
        .map((p: PersonnelMinimal) => ({
          value: p._id,
          label: p.userId ? `${p.name} (${p.userId})` : p.name,
          ...projectTag(p.appId),
        })),
    [personnel, belongsToProject, projectTag]
  );

  const searchPersonnel = async (term: string) =>
    personnelOptions.filter((o) => o.label.toLowerCase().includes(term.toLowerCase()));

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  // Fetch existing user when editing
  const { data: existing, isLoading: fetchLoading } = useQuery({
    queryKey: ['users', id],
    queryFn: async () => await usersService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  useBreadcrumbTitle(isEditMode ? existing?.userId : null);

  useEffect(() => {
    if (existing && isEditMode) {
      reset({
        personnelMasterId: existing.personnelMasterId || '',
        appId: existing.appId || '',
        userId: existing.userId || '',
        contactEmail: existing.contact?.email || '',
        contactPhone: existing.contact?.phoneNo || '',
        // MPIN/password are not editable on edit (dedicated set/reset flows). Never
        // load the stored value — existing.mpin is a bcrypt hash, which fails the
        // "4-6 digits or empty" rule and would silently block Update on a now-hidden field.
        mpin: '',
        password: '',
        defaultlanguageId:
          existing.defaultlanguageId !== undefined && existing.defaultlanguageId !== null
            ? String(existing.defaultlanguageId)
            : '',
        userStatus: existing.userStatus || '',
        reasonForBlock: existing.reasonForBlock || '',
        blocked: existing.blocked ?? false,
        isRegistered: existing.isRegistered ?? false,
        isVerified: existing.isVerified ?? false,
        isLoggedIn: existing.isLoggedIn ?? false,
      });
    }
  }, [existing, isEditMode, reset]);

  const watchedPersonnelId = watch('personnelMasterId');
  const hasPersonnel = !!(watchedPersonnelId && watchedPersonnelId.trim());

  const selectedPersonnelOption = useMemo(() => {
    if (!watchedPersonnelId) return null;
    return personnelOptions.find((o) => o.value === watchedPersonnelId) || null;
  }, [personnelOptions, watchedPersonnelId]);

  // Clear a linked personnel that no longer belongs to the chosen project.
  const prevAppIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevAppIdRef.current;
    prevAppIdRef.current = selectedAppId;
    if (prev === undefined || prev === selectedAppId || !selectedAppId) return;
    const p = personnel.find((x: PersonnelMinimal) => x._id === watchedPersonnelId);
    if (p?.appId && p.appId !== selectedAppId) setValue('personnelMasterId', '');
  }, [selectedAppId, personnel, watchedPersonnelId, setValue]);

  // Pre-fill the Project from the selected personnel: a linked personnel already
  // belongs to a project, and a user must live in that same project (backend FK
  // rule). A global personnel (no appId) leaves the Project for the user to pick.
  useEffect(() => {
    if (!watchedPersonnelId) return;
    const p = personnel.find((x: PersonnelMinimal) => x._id === watchedPersonnelId);
    if (p?.appId && p.appId !== selectedAppId) {
      setValue('appId', p.appId, { shouldValidate: true, shouldDirty: true });
      // A project-scoped personnel locks the user to its project — even the
      // multi-project create collapses to that single project.
      if (!isEditMode) setValue('appIds', [p.appId], { shouldValidate: true, shouldDirty: true });
    }
  }, [watchedPersonnelId, personnel, selectedAppId, setValue, isEditMode]);

  // A project-scoped personnel locks the project(s) — the multi-select is then
  // fixed to that one project. Global / no personnel → free multi-select.
  const lockedProjectId = useMemo(() => {
    const p = personnel.find((x: PersonnelMinimal) => x._id === watchedPersonnelId);
    return p?.appId || null;
  }, [personnel, watchedPersonnelId]);

  // Mutations
  // Create the same user across several projects in one call. Best effort: the
  // backend reports which projects failed, so we surface a per-project summary
  // instead of a single all-or-nothing success/error.
  const createMultiMut = useMutation({
    mutationFn: ({ payload, appIds }: { payload: Omit<UserCreateRequest, 'appId'>; appIds: string[] }) =>
      usersService.createMulti(payload, appIds),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      const created = res.created?.length ?? 0;
      const failed = res.failed ?? [];
      if (failed.length === 0) {
        // Full success → brief toast, then go to the list (like every other form).
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: created === 1 ? 'User created in 1 project' : `User created in ${created} projects`,
          life: 3000,
        });
        setTimeout(() => navigateToList(), 800);
      } else {
        // Partial / total failure → sticky (no auto-dismiss) so the per-project summary
        // stays until read/closed with the ✕, and we stay on the page so it can be retried.
        const projName = (aid: string) => apps.find((a: Project) => a._id === aid)?.name || aid;
        toast.current?.show({
          severity: created > 0 ? 'warn' : 'error',
          summary: created > 0 ? 'Created with some failures' : 'Create failed',
          detail: `${created} created, ${failed.length} failed: ${failed.map((f) => `${projName(f.appId)} (${f.reason})`).join('; ')}`,
          sticky: true,
        });
      }
    },
    onError: (error: any) => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: extractErrorMessage(error, 'Failed to save user'),
        life: 10000,
      });
    },
  });

  const updateMut = useMutation({
    mutationFn: (payload: UserUpdateRequest) => usersService.update(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', id] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'User updated successfully', life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: extractErrorMessage(error, 'Failed to save user'),
        life: 10000,
      });
    },
  });

  const isSaving = createMultiMut.isPending || updateMut.isPending;

  const onSubmit = (data: UserFormData) => {
    const linked = !!(data.personnelMasterId && data.personnelMasterId.trim());
    const contact = linked
      ? undefined
      : { email: (data.contactEmail || '').trim(), phoneNo: (data.contactPhone || '').trim() };

    if (isEditMode) {
      // userId is NOT updatable. personnelMasterId IS: sending it (or '' to unlink)
      // re-links or makes the account standalone.
      const payload: UserUpdateRequest = {
        personnelMasterId: linked ? data.personnelMasterId : '',
        appId: data.appId || undefined,
        defaultlanguageId:
          data.defaultlanguageId !== undefined && data.defaultlanguageId !== ''
            ? Number(data.defaultlanguageId)
            : undefined,
        userStatus: data.userStatus || undefined,
        blocked: data.blocked ?? false,
        reasonForBlock: data.reasonForBlock || undefined,
        isRegistered: data.isRegistered ?? false,
        isVerified: data.isVerified ?? false,
        isLoggedIn: data.isLoggedIn ?? false,
      };
      if (contact) payload.contact = contact;
      updateMut.mutate(payload);
    } else {
      // One create call, one user_master row per selected project. `appIds` is
      // authoritative; fall back to the single `appId` if it's somehow empty.
      const appIds = (data.appIds && data.appIds.length ? data.appIds : [data.appId]).filter(Boolean);
      const payload: Omit<UserCreateRequest, 'appId'> = {
        userId: data.userId,
        mpin: data.mpin || undefined,
        password: data.password || undefined,
        defaultlanguageId:
          data.defaultlanguageId !== undefined && data.defaultlanguageId !== ''
            ? Number(data.defaultlanguageId)
            : undefined,
      };
      if (linked) payload.personnelMasterId = data.personnelMasterId;
      else payload.contact = contact;
      createMultiMut.mutate({ payload, appIds });
    }
  };

  if (fetchLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <PermissionGuard jobName={JOB_NAMES.USER_MANAGEMENT}>
      <Toast ref={toast} position="top-right" />

      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid="SCR-UserManagement-Form">
        {/* Header — labeled back pill above the title, matching the other forms. */}
        <ScreenHeader
          icon="pi pi-users"
          title={isEditMode ? 'Edit User' : 'Add User'}
          subtitle={isEditMode ? 'Update user account information' : 'Create an app account — link a personnel, or add a standalone user with its own contact'}
          onBack={() => handleNavigate('/user-management')}
        />

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Account Section */}
          <div className="ds-card p-4 mb-3">
            <h2 className="text-base font-semibold text-ink mb-3">Account Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Personnel */}
              <Controller
                name="personnelMasterId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSearchableSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Personnel"
                    onSearch={searchPersonnel}
                    initialOptions={personnelOptions}
                    selectedOption={selectedPersonnelOption}
                    placeholder="Optional — leave empty for a standalone user"
                    error={!!errors.personnelMasterId}
                    helperText={errors.personnelMasterId?.message || 'Link a personnel, or leave empty and enter contact below'}
                  />
                )}
              />

              {/* Project — a single project on edit, but on create the user can be
                  created across several projects at once (one row per project). A
                  project-scoped personnel locks it to that one project. */}
              {isEditMode ? (
                <Controller
                  name="appId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Project"
                      required
                      filter
                      options={appOptions}
                      error={!!errors.appId}
                      helperText={
                        errors.appId?.message ||
                        (hasPersonnel ? "Set from the selected personnel's project" : undefined)
                      }
                    />
                  )}
                />
              ) : (
                // Side-by-side with Personnel. Chips wrap within the column; the option
                // panel still opens full trigger-width for easy selection.
                <Controller
                  name="appIds"
                  control={control}
                  render={({ field: { value } }) => (
                    <FormMultiSelect
                      name="appIds"
                      label="Projects"
                      required
                      value={value || []}
                      onChange={(vals) => {
                        setValue('appIds', vals, { shouldValidate: true, shouldDirty: true });
                        setValue('appId', vals[0] || '', { shouldValidate: true, shouldDirty: true });
                      }}
                      options={appOptions}
                      disabled={!!lockedProjectId}
                      error={!!errors.appId || !!errors.appIds}
                      helperText={
                        (errors.appId?.message as string) ||
                        (lockedProjectId
                          ? "Locked to the selected personnel's project"
                          : 'The user will be created in each selected project')
                      }
                    />
                  )}
                />
              )}

              {/* User ID */}
              <Controller
                name="userId"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="Username"
                    placeholder="Login username"
                    required
                    disabled={isEditMode}
                    autoComplete="off"
                    error={!!errors.userId}
                    helperText={errors.userId?.message || 'Unique login id per project'}
                  />
                )}
              />

              {/* MPIN & Password — set at creation only. On edit, credentials are
                  changed through their own set/reset flows (and planned
                  forgot-password / forgot-mpin flows), not this form. */}
              {!isEditMode && (
                <>
                  <Controller
                    name="mpin"
                    control={control}
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        value={field.value || ''}
                        label="MPIN"
                        placeholder="4-6 digit PIN"
                        autoComplete="off"
                        error={!!errors.mpin}
                        helperText={errors.mpin?.message}
                      />
                    )}
                  />

                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        value={field.value || ''}
                        type="password"
                        label="Password"
                        placeholder="Set a password"
                        autoComplete="new-password"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                      />
                    )}
                  />
                </>
              )}

              {/* Default Language */}
              <Controller
                name="defaultlanguageId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect
                    {...field}
                    value={value !== undefined && value !== null && value !== '' ? String(value) : ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Default Language"
                    options={LANGUAGE_OPTIONS}
                    error={!!errors.defaultlanguageId}
                    helperText={errors.defaultlanguageId?.message || 'Language the user sees in the app'}
                  />
                )}
              />
            </div>
          </div>

          {/* Contact Section */}
          <div className="ds-card p-4 mb-3">
            <h2 className="text-base font-semibold text-ink mb-3">Contact</h2>
            {hasPersonnel ? (
              <p className="text-sm text-ink-muted flex items-center gap-2">
                <i className="pi pi-info-circle" />
                Contact (email &amp; phone) is inherited from the selected personnel and kept in sync automatically.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Controller
                  name="contactEmail"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="Email"
                      placeholder="user@example.com"
                      required
                      error={!!errors.contactEmail}
                      helperText={errors.contactEmail?.message}
                    />
                  )}
                />
                <Controller
                  name="contactPhone"
                  control={control}
                  render={({ field }) => (
                    <PhoneField
                      name="contactPhone"
                      value={field.value || ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      label="Phone"
                      required
                      error={!!errors.contactPhone}
                      helperText={errors.contactPhone?.message}
                    />
                  )}
                />
              </div>
            )}
          </div>

          {/* Linked Sign-in Methods (edit only, read-only) */}
          {isEditMode && (
            <div className="ds-card p-4 mb-3">
              <h2 className="text-base font-semibold text-ink mb-3">Linked Sign-in Methods</h2>
              {existing?.externalLogins && existing.externalLogins.length > 0 ? (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {existing.externalLogins.map((login) => (
                    <div
                      key={`${login.provider}:${login.subject}`}
                      className="flex items-center justify-between py-2 text-sm"
                    >
                      <span className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                        <i
                          className={`pi ${login.provider === 'google' ? 'pi-google text-red-500' : 'pi-microsoft text-blue-500'}`}
                        />
                        <span className="capitalize font-medium">{login.provider}</span>
                        <span className="text-ink-muted">· {login.email}</span>
                      </span>
                      <span className="text-xs text-gray-400">
                        {login.lastLoginAt
                          ? `Last login ${new Date(login.lastLoginAt).toLocaleString()}`
                          : 'Never used'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-ink-muted flex items-center gap-2">
                  <i className="pi pi-info-circle" />
                  No Google or Microsoft accounts linked to this user.
                </p>
              )}
            </div>
          )}

          {/* Status Section (edit only) */}
          {isEditMode && (
            <div className="ds-card p-4 mb-3">
              <h2 className="text-base font-semibold text-ink mb-3">Status & Flags</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* User Status (text — no userStatus value-set seeded in BE) */}
                <Controller
                  name="userStatus"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="User Status"
                      placeholder="e.g. ACTIVE, SUSPENDED"
                      error={!!errors.userStatus}
                      helperText={errors.userStatus?.message}
                    />
                  )}
                />

                {/* Reason For Block */}
                <Controller
                  name="reasonForBlock"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="Reason For Block"
                      placeholder="Reason if blocked"
                      error={!!errors.reasonForBlock}
                      helperText={errors.reasonForBlock?.message}
                    />
                  )}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <Controller
                  name="blocked"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex items-center gap-2">
                      <InputSwitch checked={!!value} onChange={(e: any) => onChange(e.value)} />
                      <label className="text-sm text-gray-700 dark:text-gray-300">Blocked</label>
                    </div>
                  )}
                />
                <Controller
                  name="isRegistered"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex items-center gap-2">
                      <InputSwitch checked={!!value} onChange={(e: any) => onChange(e.value)} />
                      <label className="text-sm text-gray-700 dark:text-gray-300">Registered</label>
                    </div>
                  )}
                />
                <Controller
                  name="isVerified"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex items-center gap-2">
                      <InputSwitch checked={!!value} onChange={(e: any) => onChange(e.value)} />
                      <label className="text-sm text-gray-700 dark:text-gray-300">Verified</label>
                    </div>
                  )}
                />
                <Controller
                  name="isLoggedIn"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex items-center gap-2">
                      <InputSwitch checked={!!value} onChange={(e: any) => onChange(e.value)} />
                      <label className="text-sm text-gray-700 dark:text-gray-300">Logged In</label>
                    </div>
                  )}
                />
              </div>
            </div>
          )}

          {/* Actions — a proper bar so the buttons read as an intentional footer. */}
          <div className="ds-card px-4 py-3 flex gap-3 justify-end">
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              onClick={() => handleNavigate('/user-management')}
              disabled={isSaving}
            />
            <Button
              type="submit"
              label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              icon={isSaving ? 'pi pi-spin pi-spinner' : undefined}
              disabled={isSaving}
            />
          </div>
        </form>
      </div>

      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="UserManagementForm.Dialog.Leave"
      />
    </PermissionGuard>
  );
};

export default UserManagementForm;
